// Third party
import {
  GridCellParams,
  GridColDef,
  DataGridPro,
  GridFetchRowsParams,
  useGridApiRef, GridApiPro,
  GridActionsCellItemProps,
} from '@mui/x-data-grid-pro';
import Typography from '@mui/material/Typography';
import startCase from 'lodash/startCase';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { useIsFetching } from '@tanstack/react-query';

// Hooks
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import {
  ReactElement, useCallback, useMemo, useRef, MutableRefObject, useState, useEffect,
} from 'react';
import Card from '@mui/material/Card';
import { unstable_debounce as debounce } from '@mui/utils';
import { GridFilterModel } from '@mui/x-data-grid/models/gridFilterModel';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import {
  MemoizedBaseAvatar,
  Label,
  SoftChip,
} from '@mgm/common/components/base';
import { dateFormatTZ, convertToCamelCase } from '@mgm/common/utils';
import { alpha } from '@mui/material/styles';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';

// Components
import {
  MemoizedFilterTabs,
} from '../../../../components/base';

// Redux-toolkit
import { memberActions } from '../../../../store/team/slice';
import { commonActions } from '../../../../store/common/slice';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';

// Types
import { TTimeOff } from '../../../../types';
import { useTable } from '../../../../hooks/useTable';
import { getIsPrintMode } from '../../../../store/common/selectors';
import { PrintWrapper } from '../../../../layout/wrapper/PrintWrapper';
import { TimeoffTableHeader } from './TimeoffTableHeader';
import { TimeoffTableActions } from './TimeoffTableActions';
import { STATUS_ICON_MAP, STATUS_ICON_COLOR_MAP } from '../data/timeoff';
import { schedulesActions } from '../../../../store/schedules/slice';
import { useTimeoffFilter } from '../hooks/useTimeoffFilter';
import { getTimeOffs } from '../../../../store/schedules/selectors';
import { useApproveTimeOffAPI, useDeclineTimeOffAPI } from '../../../../hooks/api/useSchedules';
import { QUERY_KEYS } from '../../../../utils/enums';

const TimeoffTable = () => {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const dispatch = useAppDispatch();
  const {
    localText, handleScrollTable, handleExport, exporting,
  } = useTable(apiRef);
  const isPrintMode = useAppSelector(getIsPrintMode);
  const { mutateAsync: approveMutate } = useApproveTimeOffAPI();
  const { mutateAsync: declineMutate } = useDeclineTimeOffAPI();
  const componentRef = useRef<HTMLDivElement | null>(null);

  const isFetchingTimeoffs = useIsFetching({
    queryKey: [QUERY_KEYS.SCHEDULES, 'timeoff'],
  });

  const { getTimeoffsByFilters } = useTimeoffFilter();

  const timeoffs = useAppSelector(getTimeOffs);

  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);

  const { t } = useTranslation(['inputs', 'common', 'team', 'schedule']);

  const { pageHeight, resizableDiv, isDesktopSmallScreen } = useScreenResize(59);

  const [rows, setRows] = useState<TTimeOff[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({} as GridFilterModel);
  const [isFullTable, setIsFullTable] = useState(false);

  const getLengthByStatus = (status: string) => timeoffs.filter((timeoff) => timeoff.status === status)?.length || 0;

  const tabs = useMemo(() => [
    {
      value: 0,
      label: t('requested', { ns: 'schedule' }),
      color: 'primary',
      count: timeoffs.length,
    },
    {
      value: 1,
      label: t('approved', { ns: 'schedule' }),
      color: 'success',
      count: getLengthByStatus('Approved'),
    },
    {
      value: 2,
      label: t('rejected', { ns: 'schedule' }),
      color: 'error',
      count: getLengthByStatus('Declined'),
    },
    {
      value: 3,
      label: t('pending', { ns: 'schedule' }),
      color: 'warning',
      count: getLengthByStatus('Pending'),
    },
  ], [timeoffs]);

  const onCellDoubleClick = (params: GridCellParams) => {
    dispatch(memberActions.setSelectedMember(params.row));

    setTimeout(() => {
      dispatch(commonActions.setIsOpenPagesNav(true));
    }, 100);
  };

  const handleFilterStatus = (newValue: number) => {
    dispatch(schedulesActions.setSelectedStatus(newValue));
  };

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoData', { ns: 'common' })} />;

  const fetchRows = useCallback(
    async (params: GridFetchRowsParams) => {
      const totalRows = getTimeoffsByFilters.length;
      const firstRowToRender = params.firstRowToRender || 0;
      const lastRowToRender = Math.min(params.lastRowToRender || 10, totalRows);

      const quickFilterValues = filterModel?.quickFilterValues || [];

      let filteredRows = getTimeoffsByFilters;
      if (quickFilterValues.length > 0) {
        filteredRows = getTimeoffsByFilters.filter((timeoff) => quickFilterValues.some((value) => {
          const searchValue = value.toLowerCase();
          return (
            timeoff.name.toLowerCase().includes(searchValue)
            || timeoff.email.toLowerCase().includes(searchValue)
          );
        }));
      }

      const slice = filteredRows.slice(firstRowToRender, lastRowToRender);

      return { slice, total: filteredRows.length };
    },
    [getTimeoffsByFilters, filterModel],
  );

  useEffect(() => {
    if (getTimeoffsByFilters?.length === 0) {
      setRows([]);
      setRowCount(0);
      return;
    }

    const fetchInitialRows = async () => {
      const { slice, total } = await fetchRows({
        firstRowToRender: 0,
        lastRowToRender: 10,
        sortModel: [],
        filterModel: { items: [] },
      });
      setRows(slice);
      setRowCount(total);
    };

    fetchInitialRows();
  }, [fetchRows, getTimeoffsByFilters]);

  const handleFetchRows = useCallback(
    async (params: GridFetchRowsParams) => {
      const { slice, total } = await fetchRows(params);

      apiRef.current?.unstable_replaceRows(params.firstRowToRender, slice);
      setRowCount(total);
    },
    [apiRef, fetchRows],
  );

  const debouncedHandleFetchRows = useMemo(
    () => debounce(handleFetchRows, 200),
    [handleFetchRows],
  );

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);

    debouncedHandleFetchRows({
      firstRowToRender: 0,
      lastRowToRender: 10,
      filterModel: newFilterModel,
      sortModel: [],
    });
  };

  const timeOffColumns: Array<GridColDef> = useMemo(() => [
    {
      field: 'name',
      headerName: t('name'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <MemoizedBaseAvatar
            statusTooltipText={t('pendingRegistration', { ns: 'team' })}
            status={row.status}
            avatarFile={row.avatarFile}
            avatarName={row.name}
          />
          <Typography variant="caption">
            {startCase(row.name)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'type',
      width: 200,
      headerName: t('timeoffType', { ns: 'schedule' }),
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Label
            variant="soft"
            color={
              (row.type === 'Vacation' && 'success')
              || (row.type === 'Sick leave' && 'warning')
              || (row.type === 'Other' && 'info')
              || 'error'
            }
          >
            {t(`${convertToCamelCase(row.type)}`, { ns: 'schedule' })}
          </Label>
        </Box>
      ),
      valueGetter: (_, row) => t(`${convertToCamelCase(row.type)}`, { ns: 'schedule' }),
    },
    {
      field: 'startDate',
      headerName: t('startDateAndEndDate', { ns: 'schedule' }),
      width: 275,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {dateFormatTZ(row.start, 'DD MMM YYYY')}
            {' '}
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
              }}
            >
              to
            </Typography>
            {' '}
            {' '}
            {dateFormatTZ(row.end, 'DD MMM YYYY')}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'reason',
      headerName: t('reason', { ns: 'schedule' }),
      width: 250,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {row.note || '/'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: t('status'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <SoftChip
            icon={STATUS_ICON_MAP[row.status]}
            label={t(`${row.status?.toLowerCase()}`, { ns: 'schedule' })}
            background={alpha(theme.palette[STATUS_ICON_COLOR_MAP[row.status]]?.main || theme.palette.grey[300], 0.12)}
            textColor={theme.palette[STATUS_ICON_COLOR_MAP[row.status]]?.dark}
          />
        </Box>
      ),
    },
    {
      field: 'responder',
      headerName: t('approvedBy', { ns: 'schedule' }),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography fontWeight={600} variant="caption">
            {startCase(row.responder?.name)}
          </Typography>
        </Box>
      ),
      valueGetter: (_, row) => startCase(row.responder?.name),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('actions', { ns: 'common' }),
      disableExport: true,
      sortable: false,
      align: 'right',
      getActions: ({ row }) => {
        const handleApprove = async () => {
          const data = {
            timeoffId: row.id,
            comment: '',
          };

          await approveMutate(data);
        };

        const handleDecline = async () => {
          const data = {
            timeoffId: row.id,
            comment: '',
          };
          await declineMutate(data);
        };

        if (row?.answered) {
          return [];
        }

        return TimeoffTableActions({
          onApprove: () => handleApprove(),
          onReject: () => handleDecline(),
        }) as ReactElement<GridActionsCellItemProps>[];
      },
    },
  ], [t, personnel]);

  const renderTableHeader = useCallback(() => (
    <TimeoffTableHeader
      handleScrollTable={handleScrollTable}
      printRef={componentRef}
      apiRef={apiRef}
      isFullTable={isFullTable}
      setIsFullTable={setIsFullTable}
      handleExport={() => handleExport('mgm-timeoff')}
    />
  ), [apiRef, isFullTable]);

  return (
    <PrintWrapper componentRef={componentRef}>
      <div ref={resizableDiv}>
        <Card
          sx={{
            // eslint-disable-next-line no-nested-ternary
            height: exporting ? 'auto'
            // eslint-disable-next-line no-nested-ternary
              : (isDesktopSmallScreen ? 'auto' : isFullTable && rows?.length > 0 ? 'auto' : pageHeight !== 0 && !isPrintMode ? pageHeight : 'auto'),
            pb: 6,
            ...(isMobile && {
              height: 'auto',
              background: 'unset',
              boxShadow: 'unset',
              px: 0,
              pt: 0,
              pb: '30px',
            }),
          }}
        >
          <div id="team-locations">
            <MemoizedFilterTabs tabs={tabs} onFilterChange={handleFilterStatus} />
          </div>
          <DataGridPro
            apiRef={apiRef}
            rows={rows}
            localeText={localText}
            slots={{
              toolbar: renderTableHeader as any,
              loadingOverlay: LinearProgress as any,
              noRowsOverlay: renderCustomNoRowsOverlay,
            }}
            sx={{
              px: 0,
              '.MuiDataGrid-main > *:first-of-type': {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              },
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                printOptions: {
                  hideFooter: true,
                  hideToolbar: true,
                },
              },
            }}
            {...(!isMobile && {
              initialState: { pinnedColumns: { left: ['name'], right: ['actions'] } },
            })}
            getRowId={(row) => row.id}
            columns={timeOffColumns}
            hideFooterPagination
            loading={isFetchingTimeoffs as unknown as boolean}
            disableRowSelectionOnClick
            hideFooter
            onCellDoubleClick={onCellDoubleClick}
            rowCount={rowCount}
            sortingMode="server"
            filterMode="server"
            rowsLoadingMode="server"
            checkboxSelection={false}
            className={isPrintMode ? 'print-mode' : ''}
            showToolbar
            onFilterModelChange={handleFilterModelChange}
            onFetchRows={debouncedHandleFetchRows}
          />
        </Card>
      </div>
    </PrintWrapper>
  );
};

export {
  TimeoffTable,
};
